/**
 * Extract the flat list of permission codes from an access-management auth
 * context's `permissions` value.
 *
 * `loadEffectivePermissions()` returns `permissions` as a JSON string of
 * `[permissionCode, orgUnitPaths[]][]` — NOT the `Map` its declared type claims.
 * The auth service hides this with `as unknown as string` and only
 * `setUserAuthContext()` parses it; the NgRx effect consumes the raw result, so
 * it sees the string. This parses that string into the list of codes, returning
 * `[]` for empty/malformed input so callers never emit an invalid value.
 *
 * NOTE: the declared type of `AccessManagementUserAuthContext.permissions`
 * (`Map`) does not match what the API returns (a JSON string). The proper fix is
 * to parse at the auth-service boundary so the type is honest — tracked as a
 * separate follow-up. If that lands, update this helper and its test to read the
 * codes from the Map directly.
 */
export function extractPermissionCodes(raw: unknown): string[] {
  if (typeof raw !== 'string' || !raw.trim()) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .map((entry) => (Array.isArray(entry) ? entry[0] : undefined))
      .filter((code): code is string => typeof code === 'string');
  } catch {
    return [];
  }
}
